import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Camera, RotateCcw, Power, ChevronDown,
  Shield, Monitor, ArrowUpCircle, FileText,
  Thermometer, Wind, Network, Clock,
  AlertTriangle, Video, VideoOff, HardDrive, Trash2, Film, Gamepad2,
  ListMusic, Download, Upload, Megaphone, LayoutList, Maximize2, Filter, Zap, Activity,
  SkipBack, SkipForward, Pause, Play, Volume2, VolumeX, Terminal, Usb, ShieldOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import RemoteDesktopViewer from '@/components/RemoteDesktopViewer';
import RemoteShellViewer from '@/components/RemoteShellViewer';
import { PingButton } from '@/components/PingButton';
import { supabase } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { toast } from 'sonner';
import {
  getDemoStation, sendCommand, sendHudUpdate,
  getStationCommands, getLatestRelease, updateDemoStation, deleteDemoStation,
  formatUptime, formatLastSeen, isStationOnline,
  setDemoMode, setAdminMode, setUsbUnlocked, ADMIN_MODE_MINUTES, USB_UNLOCK_MINUTES,
  isUsbUnlocked, formatRemaining, canSendCommand,
} from '@/lib/api/demoStations';
import { fetchBuildPrimaryImage } from '@/lib/api/builds';
import { useAppSetting } from '@/hooks/useAppSettings';
import type { DemoCommandType } from '@/types/demo';
import { DEMO_STATUS_CONFIG, DEMO_COMMAND_LABELS } from '@/types/demo';

// ── Reused sub-components ────────────────────────────────────────────────────

function TempChip({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  const color =
    value >= 85 ? 'text-red-600 bg-red-50 dark:bg-red-900/20 border-red-300' :
    value >= 70 ? 'text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300' :
    'text-green-700 bg-green-50 dark:bg-green-900/20 border-green-300';
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border', color)}>
      <Thermometer className="h-3 w-3" />{label} {Math.round(value)}°C
    </span>
  );
}

function UsageBar({ label, value, subtitle }: { label: string; value: number | null; subtitle?: string }) {
  const pct = value ?? 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className="font-medium text-foreground">
          {value != null ? `${Math.round(pct)}%` : '—'}
          {subtitle && <span className="ml-1 text-muted-foreground font-normal">({subtitle})</span>}
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function formatRamMb(usedMb: number | null, totalMb: number | null): string | undefined {
  if (usedMb == null || totalMb == null) return undefined;
  const fmt = (mb: number) => mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
  return `${fmt(usedMb)} / ${fmt(totalMb)}`;
}

function formatDiskGb(usedGb: number | null, totalGb: number | null): string | undefined {
  if (usedGb == null || totalGb == null) return undefined;
  const fmt = (gb: number) => gb >= 1000 ? `${(gb / 1000).toFixed(1)} TB` : `${gb} GB`;
  return `${fmt(usedGb)} / ${fmt(totalGb)}`;
}

function VramBar({ usedMb, totalMb }: { usedMb: number | null; totalMb: number | null }) {
  if (usedMb == null || totalMb == null || totalMb === 0) return null;
  const pct = (usedMb / totalMb) * 100;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>VRAM</span>
        <span className="font-medium text-foreground">
          {(usedMb / 1024).toFixed(1)} / {(totalMb / 1024).toFixed(1)} GB
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// ── Log Viewer ──────────────────────────────────────────────────────────────

import {
  LOG_LEVELS,
  ISO_TS_RE,
  DATE_PRESETS,
  parseLogLine,
  parseLogTimestamp,
  getPresetRange,
  type DatePreset,
} from '@/lib/agentLogs';


function LogViewer({ lines, fetchedAt, onClose }: {
  lines: string[];
  fetchedAt: string;
  onClose: () => void;
}) {
  const [enabledLevels, setEnabledLevels]       = useState<Set<string>>(() => new Set(LOG_LEVELS));
  const [enabledCategories, setEnabledCategories] = useState<Set<string> | null>(null); // null = all
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Extract all categories from the log
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const line of lines) {
      const parsed = parseLogLine(line);
      if (parsed) cats.add(parsed.category);
    }
    return [...cats].sort();
  }, [lines]);

  // Initialize categories to all-enabled on first render
  useEffect(() => {
    if (enabledCategories === null && allCategories.length > 0) {
      setEnabledCategories(new Set(allCategories));
    }
  }, [allCategories, enabledCategories]);

  const toggleLevel = (l: string) => setEnabledLevels(prev => {
    const next = new Set(prev);
    next.has(l) ? next.delete(l) : next.add(l);
    return next;
  });

  const toggleCategory = (c: string) => setEnabledCategories(prev => {
    const next = new Set(prev ?? allCategories);
    next.has(c) ? next.delete(c) : next.add(c);
    return next;
  });

  const selectAllCategories = () => setEnabledCategories(new Set(allCategories));
  const selectNoneCategories = () => setEnabledCategories(new Set());

  // Compute active date range
  const dateRange = useMemo<{ from: number; to: number } | null>(() => {
    if (datePreset === 'custom') {
      const from = customFrom ? new Date(customFrom).getTime() : 0;
      const to = customTo ? new Date(customTo + 'T23:59:59').getTime() : Date.now();
      return (from || to) ? { from, to } : null;
    }
    return getPresetRange(datePreset);
  }, [datePreset, customFrom, customTo]);

  const filteredLines = useMemo(() => {
    const cats = enabledCategories ?? new Set(allCategories);
    return lines.filter(line => {
      // Date filter
      if (dateRange) {
        const ts = parseLogTimestamp(line);
        if (ts !== null && (ts < dateRange.from || ts > dateRange.to)) return false;
      }
      // Level + category filter
      const parsed = parseLogLine(line);
      if (!parsed) return true; // keep unparseable lines
      return enabledLevels.has(parsed.level) && cats.has(parsed.category);
    });
  }, [lines, enabledLevels, enabledCategories, allCategories, dateRange]);

  const levelColor = (l: string) =>
    l === 'ERROR' ? 'bg-red-500/20 text-red-400 border-red-500/40' :
    l === 'WARN'  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' :
                    'bg-blue-500/15 text-blue-400 border-blue-500/30';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4" /> Agent Log
            <span className="text-xs font-normal text-muted-foreground ml-2">
              {filteredLines.length} of {lines.length} lines · fetched {formatLastSeen(fetchedAt)}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="px-4 py-2.5 border-b bg-muted/30 shrink-0 space-y-2">
          {/* Date range */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1 mr-1">
              <Clock className="h-3 w-3" /> Date
            </span>
            {DATE_PRESETS.map(p => (
              <button key={p.key} onClick={() => setDatePreset(p.key)}
                className={cn(
                  'px-2 py-0.5 rounded text-[11px] font-medium border transition-colors',
                  datePreset === p.key
                    ? 'bg-primary/20 text-primary border-primary/40'
                    : 'bg-transparent text-muted-foreground border-border/50 hover:border-border'
                )}>
                {p.label}
              </button>
            ))}
            {datePreset === 'custom' && (
              <div className="flex items-center gap-1.5 ml-1">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="h-6 px-1.5 rounded border border-border bg-background text-[11px] text-foreground" />
                <span className="text-[11px] text-muted-foreground">to</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="h-6 px-1.5 rounded border border-border bg-background text-[11px] text-foreground" />
              </div>
            )}
          </div>

          {/* Levels */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1 mr-1">
              <Filter className="h-3 w-3" /> Level
            </span>
            {LOG_LEVELS.map(l => {
              const active = enabledLevels.has(l);
              return (
                <button key={l} onClick={() => toggleLevel(l)}
                  className={cn(
                    'px-2 py-0.5 rounded text-[11px] font-medium border transition-colors',
                    active ? levelColor(l) : 'bg-transparent text-muted-foreground/40 border-border/50 line-through'
                  )}>
                  {l}
                </button>
              );
            })}
          </div>

          {/* Categories */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-medium text-muted-foreground mr-1">Category</span>
            <button onClick={selectAllCategories} className="text-[10px] text-primary hover:underline mr-0.5">All</button>
            <button onClick={selectNoneCategories} className="text-[10px] text-primary hover:underline mr-1">None</button>
            {allCategories.map(c => {
              const active = (enabledCategories ?? new Set(allCategories)).has(c);
              return (
                <button key={c} onClick={() => toggleCategory(c)}
                  className={cn(
                    'px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors',
                    active
                      ? 'bg-gray-700 text-gray-200 border-gray-600'
                      : 'bg-transparent text-muted-foreground/40 border-border/50 line-through'
                  )}>
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Log content */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-gray-950">
          <pre className="text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-all p-4 text-gray-300">
            {filteredLines.map(line => line.replace(
              ISO_TS_RE,
              (ts) => new Date(ts).toLocaleString(),
            )).join('\n')}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Live Telemetry Dialog ───────────────────────────────────────────────────

const LIVE_DURATION = 300; // 5 min max

function LiveTelemetryDialog({ stationId, stationName, onClose }: {
  stationId: string;
  stationName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // Fast-poll the station row every 2s while this dialog is open
  const { data: station } = useQuery({
    queryKey: ['live-telemetry', stationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('demo_stations')
        .select('cpu_usage, ram_usage, ram_used_mb, ram_total_mb, disk_usage, disk_used_gb, disk_total_gb, cpu_temp, gpu_temp, gpu_usage, gpu_memory_used_mb, gpu_memory_total_mb, gpu_power_w, gpu_hotspot_temp, fan_speeds, network_bandwidth_mbps, last_seen')
        .eq('id', stationId)
        .single();
      if (error) throw error;
      return data;
    },
    refetchInterval: 5_000,
  });

  // Send live_telemetry command on mount
  useEffect(() => {
    sendCommand(stationId, 'live_telemetry', { duration_seconds: LIVE_DURATION }).catch(() => {});
    return () => {
      // Invalidate the main station query so it picks up latest data
      qc.invalidateQueries({ queryKey: ['demo-station', stationId] });
    };
  }, [stationId, qc]);

  const f = station;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[90vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Activity className="h-4 w-4 text-green-500" />
            Live Telemetry — {stationName}
            <span className="ml-auto flex items-center gap-1.5 text-[11px] font-normal text-green-600">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Polling every 3s
            </span>
          </DialogTitle>
        </DialogHeader>

        {!f ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">Loading...</div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Usage bars */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <UsageBar label="CPU" value={f.cpu_usage} />
              <UsageBar label="GPU" value={f.gpu_usage} />
              <UsageBar label="RAM" value={f.ram_usage} subtitle={formatRamMb(f.ram_used_mb, f.ram_total_mb)} />
              <UsageBar label="Disk" value={f.disk_usage} subtitle={formatDiskGb(f.disk_used_gb, f.disk_total_gb)} />
            </div>

            {/* VRAM */}
            {f.gpu_memory_used_mb != null && f.gpu_memory_total_mb != null && (
              <VramBar usedMb={f.gpu_memory_used_mb} totalMb={f.gpu_memory_total_mb} />
            )}

            {/* Temps + power */}
            <div className="flex items-center gap-2 flex-wrap">
              <TempChip label="CPU" value={f.cpu_temp} />
              <TempChip label="GPU" value={f.gpu_temp} />
              <TempChip label="GPU Hot Spot" value={f.gpu_hotspot_temp} />
              {f.gpu_power_w != null && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-purple-300 text-purple-700 bg-purple-50 dark:bg-purple-900/20">
                  <Zap className="h-3 w-3" /> GPU: {f.gpu_power_w}W
                </span>
              )}
              {f.network_bandwidth_mbps != null && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-900/20">
                  <Network className="h-3 w-3" /> BW: {f.network_bandwidth_mbps} Mbps
                </span>
              )}
            </div>

            {/* Fans */}
            {f.fan_speeds && (f.fan_speeds as { name: string; rpm: number }[]).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <Wind className="h-3 w-3" /> Fans
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {(f.fan_speeds as { name: string; rpm: number }[]).map((fan) => (
                    <div key={fan.name} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground truncate">{fan.name}</span>
                      <span className="font-mono font-medium ml-2 shrink-0">{fan.rpm.toLocaleString()} RPM</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground text-center">
              Last update: {f.last_seen ? formatLastSeen(f.last_seen) : '—'} · Auto-stops after 5 min
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function DemoStationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [lightbox, setLightbox] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [pendingCmd, setPendingCmd] = useState<{ cmd: DemoCommandType } | null>(null);
  const [showLogViewer, setShowLogViewer] = useState(false);
  const [showLiveTelemetry, setShowLiveTelemetry] = useState(false);
  const [remoteSession, setRemoteSession] = useState<{ token: string; tunnelUrl: string } | null>(null);
  const [shellSession, setShellSession] = useState<{ token: string; tunnelUrl: string } | null>(null);

  // Current user — fetch fresh on every mount so locationIds is always up to date
  const { data: userData } = useCurrentUser();
  const userName = userData?.name ?? '';
  const isRetail = userData?.role === 'retail';
  const isRetailShop = userData?.role === 'retail_shop';
  const isLimitedView = isRetail || isRetailShop;
  // Buttons for commands the database would refuse this user are not shown.
  const canSend = (cmd: DemoCommandType) => canSendCommand(userData?.role, cmd);
  const retailLocationIds = userData?.locationIds ?? [];

  const { data: station, isLoading, error } = useQuery({
    queryKey: ['demo-station', id],
    queryFn: () => getDemoStation(id!),
    refetchInterval: 5_000,
    enabled: !!id,
  });

  const { data: commands = [] } = useQuery({
    queryKey: ['demo-commands', id],
    queryFn: () => getStationCommands(id!, 30),
    refetchInterval: 5_000,
    enabled: !!id,
  });

  const { data: latestRelease } = useQuery({
    queryKey: ['agent-latest-release'],
    queryFn: getLatestRelease,
    staleTime: 60_000,
  });

  // Active playlist for this station (resolved via targeting + priority)
  const { data: activePlaylist } = useQuery<{ playlist_id: string; playlist_name: string; items: { id: string; name: string; sort_order: number; is_flagship: boolean }[] } | null>({
    queryKey: ['station-active-playlist', id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_station_active_playlist', { p_station_id: id! });
      if (error) throw error;
      return data ?? null;
    },
    refetchInterval: 30_000,
    enabled: !!id,
  });

  // Product shot for the build this station runs. Decorative, so it loads on its
  // own and a miss just leaves the header as it was.
  const { data: buildImage } = useQuery({
    queryKey: ['build-primary-image', station?.build_id, station?.build_sku],
    queryFn: () => fetchBuildPrimaryImage(station!.build_id!, station?.build_sku),
    enabled: !!station?.build_id,
    staleTime: 10 * 60 * 1000,
  });

  // Ad campaigns targeting this station
  const { data: stationCampaigns = [] } = useQuery<{ id: string; name: string; is_active: boolean; priority: number; targeting: Record<string, string[]>; asset_name: string | null; playlist_count: number; starts_at: string | null; ends_at: string | null; show_from: string | null; show_until: string | null }[]>({
    queryKey: ['station-campaigns', id, station?.location_id, station?.build_id],
    queryFn: async () => {
      const { data } = await supabase
        .from('ad_campaigns')
        .select('id, name, is_active, priority, targeting, starts_at, ends_at, show_from, show_until, asset:ad_assets(name), playlist_items:campaign_playlist_items(id)')
        .order('priority', { ascending: false });
      if (!data) return [];
      // Client-side targeting filter (mirrors station_matches_targeting logic)
      const locId = station?.location_id ?? '';
      const buildId = station?.build_id ?? '';
      const sku = station?.build_sku ?? '';
      const familyCode = sku ? sku.replace(/^AOI-/, '').charAt(0) : '';
      return (data as any[]).filter(c => {
        const t = c.targeting ?? {};
        if (t.locations?.length && !t.locations.includes(locId)) return false;
        if (t.stations?.length && !t.stations.includes(id)) return false;
        if (t.builds?.length && !t.builds.includes(buildId)) return false;
        if (t.families?.length && (!familyCode || !t.families.includes(familyCode))) return false;
        return true;
      }).map((c: any) => ({
        id: c.id,
        name: c.name,
        is_active: c.is_active,
        priority: c.priority,
        targeting: c.targeting ?? {},
        asset_name: c.asset?.name ?? null,
        playlist_count: c.playlist_items?.length ?? 0,
        starts_at: c.starts_at,
        ends_at: c.ends_at,
        show_from: c.show_from,
        show_until: c.show_until,
      }));
    },
    refetchInterval: 30_000,
    enabled: !!id && !!station,
  });

  // Which campaign the RPC actually resolves as active for this station right now
  const { data: activeCampaignId } = useQuery<string | null>({
    queryKey: ['station-active-campaign', id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_station_active_campaign', { p_station_id: id! });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      return row?.campaign_id ?? null;
    },
    refetchInterval: 30_000,
    enabled: !!id,
  });

  // Video storage: peers for this station + video metadata
  const { data: videoPeers = [] } = useQuery({
    queryKey: ['video-peers', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('video_peers')
        .select('video_id, status, progress_pct, error_message, updated_at, tunnel_url')
        .eq('station_id', id!);
      return data ?? [];
    },
    refetchInterval: 30_000,
    enabled: !!id,
  });

  // All peers for videos this station has — to show seeding/leeching relationships
  const peerVideoIds = videoPeers.map(p => p.video_id);
  const { data: allPeers = [] } = useQuery<{ station_id: string; video_id: string; status: string; station_name: string }[]>({
    queryKey: ['video-all-peers', peerVideoIds],
    queryFn: async () => {
      if (peerVideoIds.length === 0) return [];
      const { data } = await supabase
        .from('video_peers')
        .select('station_id, video_id, status, station:demo_stations!inner(name)')
        .in('video_id', peerVideoIds)
        .neq('station_id', id!);
      return (data ?? []).map((r: any) => ({
        station_id: r.station_id,
        video_id: r.video_id,
        status: r.status,
        station_name: r.station?.name ?? r.station_id.slice(0, 8),
      }));
    },
    refetchInterval: 30_000,
    enabled: peerVideoIds.length > 0,
  });

  const { data: allVideos = [] } = useQuery({
    queryKey: ['videos-lookup'],
    queryFn: async () => {
      const { data } = await supabase.from('videos').select('id, name, file_name, file_size');
      return data ?? [];
    },
    staleTime: 30_000,
  });

  const deleteStationVideo = useMutation({
    mutationFn: async (videoId: string) => {
      // Send delete_video command to the station
      await sendCommand(id!, 'delete_video', { video_id: videoId }, userName);
    },
    onSuccess: () => {
      toast.success('Video deletion command sent');
      queryClient.invalidateQueries({ queryKey: ['video-peers', id] });
      queryClient.invalidateQueries({ queryKey: ['demo-commands', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Realtime
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`station-detail-${id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_stations', filter: `id=eq.${id}` }, () => {
        queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id, queryClient]);

  // ── Smooth playback time interpolation ────────────────────────────────────
  // Client-side counter ticks every second between server syncs.
  // Freezes when paused, snaps to server value on each poll.
  const pb = station?.playback_state ?? null;
  const pbRef = useRef<{ synced: number; current: number; duration: number; paused: boolean }>({ synced: 0, current: 0, duration: 0, paused: true });
  const [interpTime, setInterpTime] = useState(0);

  // Snap to server value whenever playback_state changes
  useEffect(() => {
    if (!pb) return;
    pbRef.current = { synced: Date.now(), current: pb.current_seconds, duration: pb.duration_seconds, paused: pb.paused };
    setInterpTime(pb.current_seconds);
  }, [pb?.current_seconds, pb?.video_id, pb?.paused]);

  // Tick every second while playing
  useEffect(() => {
    if (!pb || pb.paused) return;
    const timer = setInterval(() => {
      const elapsed = (Date.now() - pbRef.current.synced) / 1000;
      const t = Math.min(pbRef.current.current + elapsed, pbRef.current.duration);
      setInterpTime(Math.floor(t));
    }, 1000);
    return () => clearInterval(timer);
  }, [pb?.video_id, pb?.paused, pb?.current_seconds]);

  const interpProgress = pb && pb.duration_seconds > 0
    ? Math.min(100, (interpTime / pb.duration_seconds) * 100)
    : (pb?.progress_pct ?? 0);

  const cmdMutation = useMutation({
    mutationFn: ({ cmd, payload }: { cmd: DemoCommandType; payload?: Record<string, unknown> }) =>
      sendCommand(id!, cmd, payload ?? {}, userName),
    onSuccess: (_d, { cmd }) => {
      toast.success(`${DEMO_COMMAND_LABELS[cmd]} command sent`);
      queryClient.invalidateQueries({ queryKey: ['demo-commands', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const startRemoteMutation = useMutation({
    mutationFn: async () => {
      const cmd = await sendCommand(id!, 'start_remote', { timeout_minutes: 30 }, userName);
      // Poll for agent to process the command and return session info
      // 45s, not 20s: the agent picks commands up on a 5s poll, and tunnel
      // verification now waits for a freshly published hostname to become
      // routable before probing it. 20s left almost no margin.
      for (let i = 0; i < 45; i++) {
        await new Promise(r => setTimeout(r, 1000));
        const { data } = await supabase.from('demo_commands')
          .select('status, result').eq('id', cmd.id).single();
        if (data?.status === 'completed' && (data.result as any)?.token) {
          return data.result as { token: string; tunnel_url: string };
        }
        if (data?.status === 'failed') throw new Error((data.result as any)?.error || 'Failed to start remote session');
      }
      throw new Error('Timeout waiting for remote session to start');
    },
    onSuccess: (result) => {
      setRemoteSession({ token: result.token, tunnelUrl: result.tunnel_url });
      toast.success('Remote desktop connected');
      queryClient.invalidateQueries({ queryKey: ['demo-commands', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const startShellMutation = useMutation({
    mutationFn: async () => {
      const cmd = await sendCommand(id!, 'start_shell', { timeout_minutes: 30 }, userName);
      // 45s, not 20s: the agent picks commands up on a 5s poll, and tunnel
      // verification now waits for a freshly published hostname to become
      // routable before probing it. 20s left almost no margin.
      for (let i = 0; i < 45; i++) {
        await new Promise(r => setTimeout(r, 1000));
        const { data } = await supabase.from('demo_commands')
          .select('status, result').eq('id', cmd.id).single();
        if (data?.status === 'completed' && (data.result as any)?.token) {
          return data.result as { token: string; tunnel_url: string };
        }
        if (data?.status === 'failed') throw new Error((data.result as any)?.error || 'Failed to start shell session');
      }
      throw new Error('Timeout waiting for shell session to start');
    },
    onSuccess: (result) => {
      setShellSession({ token: result.token, tunnelUrl: result.tunnel_url });
      toast.success('Remote shell connected (Admin)');
      queryClient.invalidateQueries({ queryKey: ['demo-commands', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { data: globalHudOpacity } = useAppSetting('hud_bg_opacity');
  const effectiveOpacity = station?.hud_bg_opacity ?? (typeof globalHudOpacity === 'number' ? globalHudOpacity : 0.92);

  const hudMutation = useMutation({
    mutationFn: () => sendHudUpdate(id!, station!.build_id!, userName),
    onSuccess: () => toast.success('HUD update sent'),
    onError: (err: Error) => toast.error(err.message),
  });

  const hudToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('demo_stations').update({ hud_enabled: enabled }).eq('id', id!);
      if (error) throw error;
      await sendCommand(id!, 'toggle_hud', { enabled }, userName);
    },
    onSuccess: (_d, enabled) => {
      toast.success(enabled ? 'HUD enabled' : 'HUD disabled');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const hudOpacityMutation = useMutation({
    mutationFn: async (opacity: number | null) => {
      const { error } = await supabase.from('demo_stations').update({ hud_bg_opacity: opacity }).eq('id', id!);
      if (error) throw error;
      // Send the effective value to the agent (resolve null → global default)
      const effective = opacity ?? (typeof globalHudOpacity === 'number' ? globalHudOpacity : 0.92);
      await sendCommand(id!, 'set_hud_opacity', { opacity: effective }, userName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const adToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase.from('demo_stations').update({ ad_enabled: enabled }).eq('id', id!);
      if (error) throw error;
      await sendCommand(id!, 'toggle_ad', { enabled }, userName);
    },
    onSuccess: (_d, enabled) => {
      toast.success(enabled ? 'Ad enabled' : 'Ad disabled');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const videoToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      await updateDemoStation(id!, { video_enabled: enabled } as any);
      await sendCommand(id!, 'toggle_video', { enabled }, userName);
    },
    onSuccess: (_d, enabled) => {
      toast.success(enabled ? 'Video enabled' : 'Video disabled');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // The two modes are independent: either can be on without the other, and each
  // returns the station to whatever it was before. The USB button stays lit
  // under Admin Mode regardless, since it holds USB open.
  const isInAdminMode = station?.admin_mode === true;
  const isInDemoMode  = station?.demo_mode === true;

  const demoModeMutation = useMutation({
    mutationFn: (enter: boolean) => setDemoMode(station!, enter, userName),
    onSuccess: (_d, enter) => {
      toast.success(enter ? 'Demo mode — kiosk unlocked, overlays hidden' : 'Kiosk mode restored');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Stations flagged admin_mode_persistent (workshop benches) never auto-relock.
  const isPersistentAdmin = station?.admin_mode_persistent === true;

  // Admin Mode: lift every restriction so vendor installers (motherboard
  // utilities in particular) can run. Auto-relocks after ADMIN_MODE_MINUTES,
  // except on a persistent station, where it stays open until someone exits.
  const adminModeMutation = useMutation({
    mutationFn: (enter: boolean) => setAdminMode(station!, enter, userName),
    onSuccess: (_d, enter) => {
      toast.success(enter
        ? (isPersistentAdmin
            ? 'Admin mode — all restrictions lifted, stays open until you exit'
            : `Admin mode — all restrictions lifted, relocks in ${ADMIN_MODE_MINUTES / 60}h`)
        : 'Admin mode ended — restrictions restored');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Reads an expiry, so a station that dropped offline mid-unlock decays to
  // locked rather than showing as open indefinitely. Admin Mode is the one case
  // with no expiry to read: it opens USB for as long as it lasts, and on a
  // persistent station stamps no deadline at all.
  const usbOn = isInAdminMode || isUsbUnlocked(station?.usb_unlocked_until);
  const usbLabel = isUsbUnlocked(station?.usb_unlocked_until)
    ? `USB on · ${formatRemaining(station?.usb_unlocked_until)}`
    : 'USB on · Admin';

  const usbMutation = useMutation({
    mutationFn: (enable: boolean) => setUsbUnlocked(id!, enable, userName),
    onSuccess: (_d, enable) => {
      toast.success(enable
        ? `USB storage enabled for ${USB_UNLOCK_MINUTES} min`
        : 'USB storage blocked');
      queryClient.invalidateQueries({ queryKey: ['demo-station', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Retail / Retail Shop: redirect if this station is not in their assigned shops
  useEffect(() => {
    if (isLimitedView && station && retailLocationIds.length > 0 && !retailLocationIds.includes(station.location_id ?? '')) {
      navigate('/demo-stations', { replace: true });
    }
  }, [isLimitedView, station, retailLocationIds, navigate]);

  const NEEDS_CONFIRM: DemoCommandType[] = ['reboot', 'shutdown', 'retire'];

  function handleCmd(cmd: DemoCommandType, payload?: Record<string, unknown>) {
    if (NEEDS_CONFIRM.includes(cmd)) { setPendingCmd({ cmd }); return; }
    cmdMutation.mutate({ cmd, payload });
  }

  const CONFIRM_CONFIG: Partial<Record<DemoCommandType, { title: string; description: string }>> = {
    reboot:   { title: 'Reboot station?', description: 'The PC will restart in 30 seconds.' },
    shutdown: { title: 'Shut down station?', description: 'The PC will power off in 30 seconds.' },
    retire:   { title: 'Retire this station?', description: 'This will wipe the demo account and factory-reset the PC. Cannot be undone.' },
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  if (error || !station) return (
    <div className="text-center py-16 text-muted-foreground">
      <Monitor className="h-12 w-12 mx-auto mb-4 opacity-40" />
      <p className="font-medium">Station not found</p>
      <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-1.5" />Back
      </Button>
    </div>
  );

  const online = isStationOnline(station);
  const effectiveStatus = online ? station.status : (station.status === 'retiring' ? 'retiring' : 'offline');
  const cfg = DEMO_STATUS_CONFIG[effectiveStatus];
  const needsUpdate = latestRelease && station.agent_version && station.agent_version !== latestRelease.version;

  return (
    <div className="space-y-6">
      {/* Header — the back link takes a full-width row of its own (w-full in a
          flex-wrap container), so the row beneath it is the product shot, the
          title and the toolbar sharing the page width rather than competing
          with a nav control for it. That is what lets the image be large
          enough to read. On phones the toolbar still drops to its own row and
          wraps within it, and the shot sits beside the title rather than above
          it. */}
      <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
        <div className="w-full">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />All Stations
          </Button>
        </div>
        {buildImage && station.build_id && (
          <Link
            to={`/builds/${station.build_id}`}
            className="shrink-0 rounded-lg border overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            title={station.build_sku ? `Open ${station.build_sku}` : 'Open build'}
          >
            <img
              src={buildImage}
              alt={station.build_sku ?? 'Build'}
              className="h-24 w-24 sm:h-40 sm:w-40 object-cover"
              loading="lazy"
            />
          </Link>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', cfg.dot, online && station.status === 'online' ? 'animate-pulse' : '')} />
            <span className={cn('text-sm font-medium', cfg.color)}>{cfg.label}</span>
            {station.status === 'retiring' && station.retire_phase && (
              <span className="text-xs text-orange-500 italic">{station.retire_phase}</span>
            )}
            {station.status === 'retiring' && !online && !isLimitedView && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs px-2 text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => {
                  if (confirm('Remove this retired station from the dashboard?')) {
                    deleteDemoStation(station.id)
                      .then(() => { toast.success('Station removed'); navigate('/demo'); })
                      .catch(() => toast.error('Failed to remove station'));
                  }
                }}
              >
                <Trash2 className="h-3 w-3 mr-1" />Remove
              </Button>
            )}
            {!isLimitedView && needsUpdate && (
              <Badge variant="outline" className="text-[11px] text-blue-700 border-blue-400 bg-blue-50 dark:bg-blue-900/20">
                agent update available
              </Badge>
            )}
            {!isLimitedView && station.pending_updates > 0 && (
              <Badge variant="outline" className="text-[11px] text-yellow-700 border-yellow-400 bg-yellow-50">
                {station.pending_updates} Windows update{station.pending_updates !== 1 ? 's' : ''}
              </Badge>
            )}
            {station.category === 'other' && (
              <Badge variant="outline" className="text-[11px] text-purple-700 border-purple-400 bg-purple-50 dark:bg-purple-900/20">
                Other
              </Badge>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-bold mt-0.5 break-words">{station.name}</h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5 flex-wrap">
            {station.location && <span>{station.location.name}</span>}
            {station.build_sku && (
              station.build_id ? (
                <Link to={`/builds/${station.build_id}`} className="font-mono text-primary font-medium hover:underline">
                  {station.build_sku}
                </Link>
              ) : (
                <span className="font-mono text-primary font-medium">{station.build_sku}</span>
              )
            )}
            {station.build_description && <span className="truncate min-w-0 max-w-full">{station.build_description}</span>}
          </div>
        </div>

        {/* Action buttons. shrink-0 on its own kept this row at its natural width,
            so on a phone it ran off the side of the page instead of wrapping.
            relative here is the anchor the More menu falls back to on phones. */}
        <div className="relative flex items-center gap-2 flex-wrap w-full sm:w-auto sm:shrink-0">
          {!isLimitedView && (
            <>
              <PingButton stationId={id!} createdBy={userName} variant="button" />
              <Button size="sm" variant="outline" onClick={() => handleCmd('screenshot')}>
                <Camera className="h-3.5 w-3.5 mr-1.5" />Screenshot
              </Button>
              {canSend('start_remote') && (
              <Button size="sm"
                variant={remoteSession ? 'default' : 'outline'}
                className={remoteSession ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                disabled={!online || startRemoteMutation.isPending}
                onClick={() => {
                  if (remoteSession) {
                    sendCommand(id!, 'stop_remote', {}, userName);
                    setRemoteSession(null);
                  } else {
                    startRemoteMutation.mutate();
                  }
                }}>
                <Monitor className="h-3.5 w-3.5 mr-1.5" />
                {startRemoteMutation.isPending ? 'Connecting...' : remoteSession ? 'End Remote' : 'Remote'}
              </Button>
              )}
              {canSend('start_shell') && (
              <Button size="sm"
                variant={shellSession ? 'default' : 'outline'}
                className={cn('hidden sm:inline-flex', shellSession && 'bg-purple-600 hover:bg-purple-700 text-white')}
                disabled={!online || startShellMutation.isPending}
                onClick={() => {
                  if (shellSession) {
                    sendCommand(id!, 'stop_shell', {}, userName);
                    setShellSession(null);
                  } else {
                    startShellMutation.mutate();
                  }
                }}>
                <Terminal className="h-3.5 w-3.5 mr-1.5" />
                {startShellMutation.isPending ? 'Connecting...' : shellSession ? 'End Shell' : 'Shell'}
              </Button>
              )}
            </>
          )}
          <Button size="sm"
            variant={isInDemoMode ? 'default' : 'outline'}
            className={isInDemoMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'text-green-700 border-green-300 hover:bg-green-50'}
            onClick={() => demoModeMutation.mutate(!isInDemoMode)}
            disabled={demoModeMutation.isPending}>
            <Gamepad2 className="h-3.5 w-3.5 mr-1.5" />{isInDemoMode ? 'Exit Demo' : 'Demo Mode'}
          </Button>
          {canSend('admin_mode') && (
            <Button size="sm"
              variant={isInAdminMode ? 'default' : 'outline'}
              className={isInAdminMode
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'text-red-700 border-red-300 hover:bg-red-50'}
              disabled={adminModeMutation.isPending}
              title={isInAdminMode
                ? (isPersistentAdmin
                    ? 'This station never auto-relocks — click to restore all kiosk restrictions now'
                    : 'Restore all kiosk restrictions now')
                : `Lift every restriction (USB, Run, Control Panel, Task Manager, Explorer shell) so vendor software can be installed${
                    isPersistentAdmin ? ' — this station stays open until someone exits' : ''}`}
              onClick={() => adminModeMutation.mutate(!isInAdminMode)}>
              <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
              {isInAdminMode ? 'Exit Admin' : 'Admin Mode'}
            </Button>
          )}
          {/* Retail staff get this one: the shop floor needs USB for a customer's
              save files or a vendor stick, and it never drops the kiosk shell. */}
          <Button size="sm"
            variant={usbOn ? 'default' : 'outline'}
            className={cn('hidden sm:inline-flex', usbOn && 'bg-blue-600 hover:bg-blue-700 text-white')}
            disabled={!online || usbMutation.isPending}
            title={usbOn
              ? (isInAdminMode
                  ? 'USB is open because Admin Mode is on; it closes when Admin Mode ends'
                  : 'USB storage is open - click to block it again now')
              : `Allow USB storage for ${USB_UNLOCK_MINUTES} minutes without unlocking the kiosk`}
            onClick={() => usbMutation.mutate(!usbOn)}>
            <Usb className="h-3.5 w-3.5 mr-1.5" />
            {usbOn ? usbLabel : 'Enable USB'}
          </Button>
          <Button size="sm" variant="outline" className="hidden sm:inline-flex text-yellow-700 border-yellow-300 hover:bg-yellow-50"
            onClick={() => handleCmd('reboot')}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reboot
          </Button>

          {/* More actions. On phones the buttons wrap, so More can end up at the
              left of a row — anchoring the menu to the button there would push it
              off the left edge. Stay static so it anchors to the full-width row
              instead, and only anchor to the button itself from sm up. */}
          <div className="static sm:relative">
            <Button size="sm" variant="outline" onClick={() => setShowMore(!showMore)}>
              More <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
            {showMore && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMore(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border rounded-md shadow-lg py-1 min-w-[180px] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto">
                  {/* Phone-only. Shell, USB and Reboot are buttons in the toolbar
                      from sm up; below that they fold in here, which is what keeps
                      the toolbar to two wrapped rows instead of four. Hidden rather
                      than moved so the desktop toolbar is untouched. */}
                  <div className="sm:hidden">
                    {canSend('start_shell') && (
                      <button
                        className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', shellSession ? 'text-purple-600' : '')}
                        disabled={!online || startShellMutation.isPending}
                        onClick={() => {
                          if (shellSession) {
                            sendCommand(id!, 'stop_shell', {}, userName);
                            setShellSession(null);
                          } else {
                            startShellMutation.mutate();
                          }
                          setShowMore(false);
                        }}>
                        <Terminal className="h-3 w-3" />
                        {startShellMutation.isPending ? 'Connecting...' : shellSession ? 'End Shell' : 'Shell'}
                      </button>
                    )}
                    <button
                      className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', usbOn ? 'text-blue-600' : '')}
                      disabled={!online || usbMutation.isPending}
                      onClick={() => { usbMutation.mutate(!usbOn); setShowMore(false); }}>
                      <Usb className="h-3 w-3" />
                      {usbOn ? usbLabel : 'Enable USB'}
                    </button>
                    <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-yellow-700"
                      onClick={() => { handleCmd('reboot'); setShowMore(false); }}>
                      <RotateCcw className="h-3 w-3" />Reboot
                    </button>
                    <hr className="my-1 border-gray-100 dark:border-gray-800" />
                  </div>
                  {!isLimitedView && station.build_id && (
                    <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-purple-600"
                      onClick={() => { hudMutation.mutate(); setShowMore(false); }}>
                      <Monitor className="h-3 w-3" />Push HUD
                    </button>
                  )}
                  <button
                    className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', station.hud_enabled ? 'text-orange-600' : 'text-green-600')}
                    onClick={() => { hudToggleMutation.mutate(!station.hud_enabled); setShowMore(false); }}
                    disabled={hudToggleMutation.isPending}
                    title={!online ? 'Station is offline — this will apply once it reconnects' : undefined}>
                    <Monitor className="h-3 w-3" />{station.hud_enabled ? 'Disable HUD' : 'Enable HUD'}
                    {!online ? (
                      <span className="ml-auto text-[10px] text-muted-foreground">queued</span>
                    ) : station.hud_window_visible != null && station.hud_window_visible !== station.hud_enabled && (
                      <span className="ml-auto text-[10px] text-amber-500"
                        title={station.hud_window_visible ? "Requested off, but the agent reports the HUD window is still showing" : "Requested on, but the agent reports the HUD window isn't showing"}>
                        {station.hud_window_visible ? 'still showing' : 'not showing'}
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-muted-foreground"
                    onClick={e => e.stopPropagation()}>
                    <Monitor className="h-3 w-3 shrink-0" />
                    <span className="shrink-0">Opacity</span>
                    <input type="range" min="10" max="100" step="10"
                      className="flex-1 h-1 accent-blue-500 cursor-pointer"
                      value={Math.round(effectiveOpacity * 100)}
                      onChange={e => {
                        const val = parseInt(e.target.value) / 100;
                        hudOpacityMutation.mutate(val);
                      }} />
                    <span className="w-7 text-right tabular-nums">{Math.round(effectiveOpacity * 100)}%</span>
                  </div>
                  {station.hud_bg_opacity != null && (
                    <button className="flex items-center gap-2 w-full px-3 py-1 text-[10px] hover:bg-gray-50 dark:hover:bg-gray-800 text-blue-500"
                      onClick={() => hudOpacityMutation.mutate(null)}>
                      <RotateCcw className="h-2.5 w-2.5" />Reset to global default
                    </button>
                  )}
                  <button
                    className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', station.video_enabled === false ? 'text-green-600' : 'text-orange-600')}
                    onClick={() => { videoToggleMutation.mutate(station.video_enabled === false); setShowMore(false); }}
                    disabled={videoToggleMutation.isPending}
                    title={!online ? 'Station is offline — this will apply once it reconnects' : undefined}>
                    {station.video_enabled === false ? <Video className="h-3 w-3" /> : <VideoOff className="h-3 w-3" />}
                    {station.video_enabled === false ? 'Enable Video' : 'Disable Video'}
                    {!online && <span className="ml-auto text-[10px] text-muted-foreground">queued</span>}
                  </button>
                  <button
                    className={cn('flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800', station.ad_enabled ? 'text-orange-600' : 'text-green-600')}
                    onClick={() => { adToggleMutation.mutate(!station.ad_enabled); setShowMore(false); }}
                    disabled={adToggleMutation.isPending}
                    title={!online ? 'Station is offline — this will apply once it reconnects' : undefined}>
                    <Megaphone className="h-3 w-3" />{station.ad_enabled ? 'Disable Ad' : 'Enable Ad'}
                    {!online ? (
                      <span className="ml-auto text-[10px] text-muted-foreground">queued</span>
                    ) : station.ad_window_visible != null && station.ad_window_visible !== station.ad_enabled && (
                      <span className="ml-auto text-[10px] text-amber-500"
                        title={station.ad_window_visible ? "Requested off, but the agent reports the ad window is still showing" : "Requested on, but the agent reports the ad window isn't showing"}>
                        {station.ad_window_visible ? 'still showing' : 'not showing'}
                      </span>
                    )}
                  </button>
                  {!isLimitedView && (
                    <>
                      <hr className="my-1 border-gray-100 dark:border-gray-800" />
                      {canSend('update_windows') && (
                      <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => { cmdMutation.mutate({ cmd: 'update_windows', payload: {} }); setShowMore(false); }}>
                        <Shield className="h-3 w-3 text-blue-500" />Windows Update
                      </button>
                      )}
                      {needsUpdate && canSend('update_agent') && (
                        <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-blue-600"
                          onClick={() => {
                            if (latestRelease) cmdMutation.mutate({ cmd: 'update_agent', payload: { version: latestRelease.version, download_url: latestRelease.download_url } });
                            setShowMore(false);
                          }}>
                          <ArrowUpCircle className="h-3 w-3" />Update Agent
                        </button>
                      )}
                      <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-muted-foreground"
                        onClick={() => { handleCmd('get_logs', { lines: 5000 }); setShowMore(false); }}>
                        <FileText className="h-3 w-3" />Get Logs
                      </button>
                      <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-muted-foreground"
                        onClick={() => { handleCmd('get_sensors'); setShowMore(false); }}>
                        <FileText className="h-3 w-3" />Get Sensors (debug)
                      </button>
                    </>
                  )}
                  <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-orange-600"
                    onClick={() => { handleCmd('shutdown'); setShowMore(false); }}>
                    <Power className="h-3 w-3" />Shutdown
                  </button>
                  {canSend('retire') && (
                    <>
                      <hr className="my-1 border-gray-100 dark:border-gray-800" />
                      <button className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-red-600"
                        onClick={() => { handleCmd('retire'); setShowMore(false); }}>
                        <AlertTriangle className="h-3 w-3" />Retire Station
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left column — screenshot + telemetry */}
        <div className="lg:col-span-2 space-y-6">

          {/* Live View — Remote Desktop or Screenshot (hidden for retail_shop) */}
          {!isRetailShop && (
            remoteSession ? (
              <RemoteDesktopViewer
                tunnelUrl={remoteSession.tunnelUrl}
                sessionToken={remoteSession.token}
                onDisconnect={() => {
                  setRemoteSession(null);
                  sendCommand(id!, 'stop_remote', {}, userName);
                }}
                onError={(err) => { toast.error(err); setRemoteSession(null); }}
              />
            ) : (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Live View</h2>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {station.screenshot_at && <span><Clock className="h-3 w-3 inline mr-1" />{formatLastSeen(station.screenshot_at)}</span>}
                      {!isRetail && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                          onClick={() => handleCmd('screenshot')}>
                          <Camera className="h-3 w-3 mr-1" />Refresh
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {station.screenshot_url ? (
                    <button className="w-full rounded overflow-hidden border cursor-zoom-in group relative"
                      onClick={() => setLightbox(true)}>
                      <img src={station.screenshot_url} alt="Station screenshot"
                        className={cn("w-full object-cover object-top transition-all duration-500 group-hover:opacity-90", !online && "grayscale")} />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                        <span className="bg-black/60 text-white text-xs px-2 py-1 rounded">Click to expand</span>
                      </div>
                    </button>
                  ) : (
                    <div className="h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded border border-dashed text-muted-foreground text-sm">
                      No screenshot yet — click Refresh to capture one
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          )}

          {/* Remote Shell (hidden for retail_shop) */}
          {!isRetailShop && shellSession && (
            <RemoteShellViewer
              tunnelUrl={shellSession.tunnelUrl}
              sessionToken={shellSession.token}
              onDisconnect={() => {
                setShellSession(null);
                sendCommand(id!, 'stop_shell', {}, userName);
              }}
              onError={(err) => { toast.error(err); setShellSession(null); }}
            />
          )}

          {/* Telemetry */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Telemetry</h2>
                {online && (
                  <button
                    onClick={() => setShowLiveTelemetry(true)}
                    className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded border border-green-300 text-green-700 bg-green-50 hover:bg-green-100 dark:border-green-700 dark:text-green-400 dark:bg-green-900/20 dark:hover:bg-green-900/40 transition-colors"
                  >
                    <Activity className="h-3 w-3" /> Live
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* CPU / RAM / Disk usage bars */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <UsageBar label="CPU" value={station.cpu_usage} />
                <UsageBar label="RAM" value={station.ram_usage} subtitle={formatRamMb(station.ram_used_mb, station.ram_total_mb)} />
                <UsageBar label="Disk" value={station.disk_usage} subtitle={formatDiskGb(station.disk_used_gb, station.disk_total_gb)} />
              </div>

              {/* GPU usage + VRAM bars */}
              {(station.gpu_usage != null || station.gpu_memory_used_mb != null) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <UsageBar label="GPU" value={station.gpu_usage} />
                  <VramBar usedMb={station.gpu_memory_used_mb} totalMb={station.gpu_memory_total_mb} />
                </div>
              )}

              {/* Temps + power + network */}
              <div className="flex items-center gap-2 flex-wrap">
                <TempChip label="CPU" value={station.cpu_temp} />
                <TempChip label="GPU" value={station.gpu_temp} />
                <TempChip label="GPU Hot Spot" value={station.gpu_hotspot_temp} />
                {station.gpu_power_w != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-purple-300 text-purple-700 bg-purple-50 dark:bg-purple-900/20">
                    <Zap className="h-3 w-3" />
                    GPU: {station.gpu_power_w}W
                  </span>
                )}
                {station.network_speed_mbps != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-900/20">
                    <Network className="h-3 w-3" />
                    Link: {station.network_speed_mbps >= 1000 ? `${station.network_speed_mbps / 1000} Gbps` : `${station.network_speed_mbps} Mbps`}
                  </span>
                )}
                {station.network_bandwidth_mbps != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-900/20">
                    <Network className="h-3 w-3" />
                    BW: {station.network_bandwidth_mbps} Mbps
                  </span>
                )}
              </div>

              {/* Fan speeds — one row per fan with name */}
              {station.fan_speeds && station.fan_speeds.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                    <Wind className="h-3 w-3" />Fans
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {station.fan_speeds.map((f) => (
                      <div key={f.name} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground truncate">{f.name}</span>
                        <span className="font-mono font-medium ml-2 shrink-0">{f.rpm.toLocaleString()} RPM</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LHM not running warning */}
              {online && station.cpu_temp == null && station.gpu_temp == null && !station.fan_speeds?.length && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                  Hardware sensors unavailable — LibreHardwareMonitor may not be running
                </p>
              )}
            </CardContent>
          </Card>

          {/* Windows Update result — last completed update_windows command */}
          {!isLimitedView && (() => {
            const wuCmd = commands.find(c => c.command_type === 'update_windows' && (c.status === 'completed' || c.status === 'running'));
            if (!wuCmd) return null;
            const res = wuCmd.result as any;
            const isRunning = wuCmd.status === 'running';
            return (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-blue-500" /> Windows Update
                    </h2>
                    <span className="text-xs text-muted-foreground">{formatLastSeen(wuCmd.completed_at ?? wuCmd.created_at)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  {isRunning ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                        {!res?.phase || res.phase === 'started' || res.phase === 'scanning' ? 'Scanning for updates...' :
                         res.phase === 'downloading' ? `Downloading ${res.found} update${res.found === 1 ? '' : 's'}...` :
                         res.phase === 'installing' ? `Installing ${res.found} update${res.found === 1 ? '' : 's'}...` :
                         'Processing updates...'}
                      </div>
                      {res?.titles?.length > 0 && (
                        <ul className="space-y-0.5 pl-2 border-l-2 border-blue-300 dark:border-blue-700">
                          {(res.titles as string[]).map((t: string) => (
                            <li key={t} className="text-[11px] text-muted-foreground leading-snug">{t}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : res ? (
                    <div className="space-y-2 text-xs">
                      {res.error ? (
                        <p className="text-red-500">{res.error}</p>
                      ) : (
                        <>
                          <div className="flex items-center gap-4">
                            <span>Found: <span className="font-medium">{res.found ?? 0}</span></span>
                            <span>Installed: <span className="font-medium text-green-600">{res.installed ?? 0}</span></span>
                            {res.reboot_needed && <span className="text-orange-600 font-medium">Reboot needed</span>}
                          </div>
                          {res.titles?.length > 0 && (
                            <ul className="space-y-0.5 pl-2 border-l-2 border-blue-300 dark:border-blue-700">
                              {(res.titles as string[]).map((t: string) => (
                                <li key={t} className="text-[11px] text-muted-foreground leading-snug">{t}</li>
                              ))}
                            </ul>
                          )}
                          {res.found === 0 && <p className="text-green-600">System is up to date</p>}
                        </>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })()}

          {/* Command history — admin only */}
          {!isLimitedView && (
            <Card>
              <CardHeader className="pb-2">
                <h2 className="text-sm font-semibold">Command History</h2>
              </CardHeader>
              <CardContent>
                {commands.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No commands sent yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {commands.map((cmd) => (
                      <div key={cmd.id} className="flex items-center gap-3 text-sm py-1 border-b last:border-0">
                        <span className={cn('h-2 w-2 rounded-full shrink-0',
                          cmd.status === 'completed' ? 'bg-green-500' :
                          cmd.status === 'failed'    ? 'bg-red-500' :
                          cmd.status === 'running'   ? 'bg-blue-500 animate-pulse' : 'bg-gray-400'
                        )} />
                        <span className="font-medium text-xs">{DEMO_COMMAND_LABELS[cmd.command_type as DemoCommandType] ?? cmd.command_type}</span>
                        <span className="text-xs text-muted-foreground">
                          {cmd.status === 'completed' ? 'Completed' : cmd.status === 'failed' ? 'Failed' : cmd.status === 'running' ? 'Running…' : 'Pending'}
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">{formatLastSeen(cmd.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Sensor debug result — shown when a get_sensors command has completed (admin only) */}
          {!isLimitedView && (() => {
            const sensorCmd = commands.find(c => c.command_type === 'get_sensors' && c.status === 'completed' && c.result);
            if (!sensorCmd) return null;
            const res = sensorCmd.result as any;
            const sensors: { type: string; name: string; parent: string; value: number }[] = res?.sensors ?? [];
            return (
              <Card>
                <CardHeader className="pb-2">
                  <h2 className="text-sm font-semibold flex items-center justify-between">
                    <span>LHM Sensors ({res?.sensor_count ?? 0})</span>
                    <span className="text-xs font-normal text-muted-foreground">{formatLastSeen(sensorCmd.completed_at ?? sensorCmd.created_at)}</span>
                  </h2>
                </CardHeader>
                <CardContent>
                  {!res?.lhm_running ? (
                    <p className="text-xs text-red-500">LHM not running — hardware sensor data unavailable</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] font-mono">
                        <thead>
                          <tr className="text-muted-foreground border-b">
                            <th className="text-left py-1 pr-3">Type</th>
                            <th className="text-left py-1 pr-3">Name</th>
                            <th className="text-left py-1 pr-3">Parent</th>
                            <th className="text-right py-1">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sensors.map((s, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="py-0.5 pr-3 text-muted-foreground">{s.type}</td>
                              <td className="py-0.5 pr-3">{s.name}</td>
                              <td className="py-0.5 pr-3 text-muted-foreground truncate max-w-[120px]">{s.parent}</td>
                              <td className="py-0.5 text-right">{s.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Right column — system info */}
        <div className="space-y-6">

          {/* System info */}
          <Card>
            <CardHeader className="pb-2">
              <h2 className="text-sm font-semibold">System</h2>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {([
                { label: 'Hostname',   value: station.hostname, adminOnly: true },
                { label: 'IP',         value: station.ip_address, adminOnly: true },
                { label: 'Uptime',     value: station.uptime_seconds ? `${formatUptime(station.uptime_seconds)}${online ? '' : ' (at last seen)'}` : null },
                { label: 'Last seen',  value: formatLastSeen(station.last_seen) },
                { label: 'Agent',      value: station.agent_version ? `v${station.agent_version}` : null, adminOnly: true },
                { label: 'OS',         value: station.os_version },
                { label: 'Version',    value: station.os_build, adminOnly: true },
                { label: 'Serial',     value: station.serial_number, adminOnly: true },
                { label: 'Machine ID', value: station.machine_id, mono: true, truncate: true, adminOnly: true },
              ] as { label: string; value: string | null | undefined; mono?: boolean; truncate?: boolean; adminOnly?: boolean }[])
              .filter(item => !item.adminOnly || !isLimitedView)
              .map(({ label, value, mono, truncate }) => value ? (
                <div key={label} className="flex items-start justify-between gap-2">
                  <span className="text-xs text-muted-foreground shrink-0">{label}</span>
                  <span className={cn('text-xs text-right', mono && 'font-mono', truncate && 'truncate max-w-[160px]')}>{value}</span>
                </div>
              ) : null)}
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader className="pb-2">
              <h2 className="text-sm font-semibold">Status</h2>
            </CardHeader>
            <CardContent className="space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Connection</span>
                <span className={cn('font-medium', cfg.color)}>{cfg.label}</span>
              </div>
              {!isLimitedView && station.pending_updates > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span>Windows updates</span>
                    <span className="font-medium text-yellow-600">{station.pending_updates} pending</span>
                  </div>
                  {station.pending_update_titles && station.pending_update_titles.length > 0 && (
                    <ul className="space-y-0.5 pl-2 border-l-2 border-yellow-300 dark:border-yellow-700">
                      {station.pending_update_titles.map((title) => (
                        <li key={title} className="text-[11px] text-muted-foreground leading-snug">{title}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {!isLimitedView && latestRelease && (
                <div className="flex items-center justify-between">
                  <span>Agent version</span>
                  <span className={cn('font-medium font-mono', needsUpdate ? 'text-blue-600' : 'text-green-600')}>
                    v{station.agent_version ?? '—'}
                    {needsUpdate && ` → v${latestRelease.version}`}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span>Build assigned</span>
                {station.build_sku
                  ? <span className="font-medium font-mono text-primary">{station.build_sku}</span>
                  : <span className="italic">None</span>}
              </div>
              <div className="flex items-center justify-between">
                <span>Location</span>
                <span className="font-medium">{station.location?.name ?? '—'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Active Playlist (hidden for retail, shown for retail_shop) */}
          {!isRetail && activePlaylist && (() => {
            const pb = station?.playback_state;
            const online = isStationOnline(station);
            const nowPlayingIdx = pb ? pb.index : -1;
            return (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold flex items-center gap-1.5">
                      <ListMusic className="h-3.5 w-3.5" /> Active Playlist
                    </h2>
                    {pb && online && (
                      <span className="text-[10px] text-muted-foreground">
                        {pb.paused ? 'Paused' : 'Playing'} · {pb.index + 1}/{pb.total}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm font-medium">{activePlaylist.playlist_name}</p>

                  {/* Playback controls */}
                  {online && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => sendCommand(id!, 'playlist_control', { action: 'prev' })}
                        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        title="Previous"
                      >
                        <SkipBack className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => sendCommand(id!, 'playlist_control', { action: pb?.paused ? 'resume' : 'pause' })}
                        className="p-2 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                        title={pb?.paused ? 'Resume' : 'Pause'}
                      >
                        {pb?.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => sendCommand(id!, 'playlist_control', { action: 'next' })}
                        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        title="Next"
                      >
                        <SkipForward className="h-4 w-4" />
                      </button>
                      {/* Volume */}
                      <div className="flex items-center gap-1.5 ml-auto">
                        <button
                          onClick={() => sendCommand(id!, 'playlist_control', { action: 'volume', value: pb?.volume === 0 ? 0.5 : 0 })}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          title={pb?.volume === 0 ? 'Unmute' : 'Mute'}
                        >
                          {pb?.volume === 0 ? <VolumeX className="h-3.5 w-3.5 text-muted-foreground" /> : <Volume2 className="h-3.5 w-3.5 text-muted-foreground" />}
                        </button>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={pb?.volume ?? 0}
                          onChange={e => sendCommand(id!, 'playlist_control', { action: 'volume', value: parseFloat(e.target.value) })}
                          className="w-16 h-1.5 accent-primary cursor-pointer"
                          title={`Volume: ${Math.round((pb?.volume ?? 0) * 100)}%`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Progress bar */}
                  {pb && online && (
                    <div className="space-y-1">
                      <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all duration-1000" style={{ width: `${interpProgress}%` }} />
                      </div>
                      {pb.duration_seconds > 0 && (
                        <div className="flex justify-between text-[10px] text-muted-foreground">
                          <span>{Math.floor(interpTime / 60)}:{String(interpTime % 60).padStart(2, '0')}</span>
                          <span>{Math.floor(pb.duration_seconds / 60)}:{String(pb.duration_seconds % 60).padStart(2, '0')}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Track list */}
                  <div className="space-y-0.5">
                    {activePlaylist.items?.map((item, i) => {
                      const isNowPlaying = i === nowPlayingIdx && online;
                      return (
                        <div key={item.id} className={cn(
                          'flex items-center gap-2 text-xs py-1 px-1.5 rounded-md transition-colors',
                          isNowPlaying ? 'bg-primary/10 dark:bg-primary/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        )}>
                          <span className={cn('w-4 text-right shrink-0', isNowPlaying ? 'text-primary font-bold' : 'text-muted-foreground')}>{i + 1}.</span>
                          {isNowPlaying ? (
                            <span className="shrink-0 flex items-center gap-0.5">
                              <span className="inline-block w-0.5 h-2.5 bg-primary rounded-full animate-pulse" />
                              <span className="inline-block w-0.5 h-3.5 bg-primary rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                              <span className="inline-block w-0.5 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                            </span>
                          ) : (
                            <Film className="h-3 w-3 text-muted-foreground shrink-0" />
                          )}
                          <span className={cn('truncate', isNowPlaying && 'font-medium text-primary')}>{item.name}</span>
                          {item.is_flagship && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">Flagship</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          {!isLimitedView && !activePlaylist && videoPeers.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <h2 className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
                  <ListMusic className="h-3.5 w-3.5" /> No playlist assigned
                </h2>
              </CardHeader>
            </Card>
          )}

          {/* Ad Campaigns */}
          {!isLimitedView && stationCampaigns.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold flex items-center gap-1.5">
                    <Megaphone className="h-3.5 w-3.5" /> Ad Campaigns
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {stationCampaigns.filter(c => c.is_active).length} active · {stationCampaigns.length} total
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {stationCampaigns.map(c => {
                    const isActive = c.id === activeCampaignId;
                    const now = new Date();
                    const notStarted = c.starts_at && new Date(c.starts_at) > now;
                    const ended = c.ends_at && new Date(c.ends_at) < now;
                    const isPlaylist = c.playlist_count > 0;
                    return (
                      <div key={c.id} className={cn('flex items-center gap-2 py-1.5 px-2 rounded-md border', isActive ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-transparent')}>
                        <span className={cn('h-2 w-2 rounded-full flex-shrink-0', isActive ? 'bg-green-500 animate-pulse' : c.is_active ? 'bg-yellow-400' : 'bg-gray-300')} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium truncate">{c.name}</span>
                            {isActive && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">Playing</span>}
                            {!c.is_active && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-800">Paused</span>}
                            {notStarted && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">Scheduled</span>}
                            {ended && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-800">Ended</span>}
                            {c.priority > 0 && <span className="text-[9px] text-muted-foreground">P{c.priority}</span>}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                            {isPlaylist
                              ? <span className="flex items-center gap-0.5"><LayoutList className="h-2.5 w-2.5" /> {c.playlist_count} slides</span>
                              : c.asset_name && <span className="truncate">{c.asset_name}</span>
                            }
                            {c.show_from && c.show_until && <span>{c.show_from}–{c.show_until}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Video Storage */}
          {!isLimitedView && videoPeers.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold flex items-center gap-1.5">
                    <HardDrive className="h-3.5 w-3.5" /> Video Storage
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {videoPeers.filter(p => p.status === 'seeding').length}/{videoPeers.length} cached
                    {' · '}
                    {(() => {
                      const total = videoPeers.reduce((sum, p) => {
                        const v = allVideos.find(v => v.id === p.video_id);
                        return sum + (p.status === 'seeding' ? (v?.file_size ?? 0) : 0);
                      }, 0);
                      return total >= 1e9 ? `${(total / 1e9).toFixed(1)} GB` : `${Math.round(total / 1e6)} MB`;
                    })()}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {videoPeers.map(p => {
                    const v = allVideos.find(x => x.id === p.video_id);
                    const statusCfg =
                      p.status === 'seeding'     ? { label: 'Cached',      dot: 'bg-green-500', color: 'text-green-600' } :
                      p.status === 'downloading'  ? { label: `${p.progress_pct}%`, dot: 'bg-blue-500 animate-pulse', color: 'text-blue-600' } :
                      p.status === 'error'         ? { label: 'Error',       dot: 'bg-red-500',   color: 'text-red-500' } :
                                                     { label: p.status,      dot: 'bg-gray-400',  color: 'text-muted-foreground' };
                    // Other stations with this video
                    const otherPeers = allPeers.filter(op => op.video_id === p.video_id);
                    const seeders   = otherPeers.filter(op => op.status === 'seeding');
                    const leechers  = otherPeers.filter(op => op.status === 'downloading');
                    return (
                      <div key={p.video_id} className="py-1.5 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <Film className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{v?.name ?? 'Unknown'}</p>
                            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                              <span>{v?.file_size ? (v.file_size >= 1e9 ? `${(v.file_size / 1e9).toFixed(1)} GB` : `${Math.round(v.file_size / 1e6)} MB`) : '—'}</span>
                              <span className={cn('flex items-center gap-1', statusCfg.color)}>
                                <span className={cn('h-1.5 w-1.5 rounded-full', statusCfg.dot)} />
                                {statusCfg.label}
                              </span>
                            </div>
                          </div>
                          {/* Pull the copy on this PC's disk, over the station's
                              own tunnel — not the master in R2. The agent
                              refreshes tunnel_url every 5 minutes, so a row
                              older than that is pointing at a hostname that may
                              already be gone. */}
                          {p.status === 'seeding' && (() => {
                            const tunnelFresh = !!p.tunnel_url && online
                              && Date.now() - new Date(p.updated_at).getTime() < 10 * 60 * 1000;
                            const fileName = v?.file_name ?? `${p.video_id}.mp4`;
                            if (!tunnelFresh) return (
                              <span
                                className="p-1 flex-shrink-0 opacity-30"
                                title={online
                                  ? 'No live tunnel for this station yet — it publishes one every 5 minutes while online'
                                  : 'Station is offline — the file can only be pulled while it is running'}>
                                <Download className="h-3.5 w-3.5" />
                              </span>
                            );
                            return (
                              <a
                                href={`${p.tunnel_url}/v/${p.video_id}?download=1&name=${encodeURIComponent(fileName)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 flex-shrink-0"
                                title={`Download this station's copy of ${fileName} over its tunnel`}>
                                <Download className="h-3.5 w-3.5 text-blue-400 hover:text-blue-500" />
                              </a>
                            );
                          })()}
                          {p.status === 'seeding' && (
                            <button
                              onClick={() => { if (confirm(`Delete "${v?.name ?? 'this video'}" from this station? It will re-download on next sync.`)) deleteStationVideo.mutate(p.video_id); }}
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                              title="Delete from this station (will re-download)">
                              <Trash2 className="h-3.5 w-3.5 text-red-400 hover:text-red-500" />
                            </button>
                          )}
                        </div>
                        {/* Peer relationships */}
                        {otherPeers.length > 0 && (
                          <div className="ml-5 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                            {seeders.length > 0 && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Upload className="h-2.5 w-2.5" />
                                Seeding: {seeders.map(s => s.station_name).join(', ')}
                              </span>
                            )}
                            {leechers.length > 0 && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Download className="h-2.5 w-2.5" />
                                Downloading: {leechers.map(l => l.station_name).join(', ')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logs inline — shows result of last get_logs (admin only) */}
          {!isLimitedView && (() => {
            const logsCmd = commands.find(c => c.command_type === 'get_logs' && c.status === 'completed' && c.result);
            if (!logsCmd) return null;
            const res = logsCmd.result as any;
            const lines: string[] = res?.lines ?? [];
            return (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Agent Log</h2>
                    <div className="flex items-center gap-2">
                      {lines.length > 0 && (
                        <button
                          onClick={() => setShowLogViewer(true)}
                          className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded border border-border hover:bg-muted transition-colors text-muted-foreground"
                        >
                          <Maximize2 className="h-3 w-3" /> Open Viewer
                        </button>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {lines.length > 0 ? `${lines.length} lines · ` : ''}{formatLastSeen(logsCmd.completed_at ?? logsCmd.created_at)}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {res?.error ? (
                    <p className="text-xs text-red-500 px-3 pb-3">Error reading log: {res.error}</p>
                  ) : lines.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 pb-3">Log file is empty — agent may not have written any entries yet.</p>
                  ) : (
                    <pre className="text-[10px] font-mono leading-relaxed whitespace-pre-wrap break-all p-3 bg-gray-950 text-gray-300 rounded-b-lg max-h-64 overflow-y-auto">
                      {lines.map(line => line.replace(
                        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/,
                        (ts) => new Date(ts).toLocaleString(),
                      )).join('\n')}
                    </pre>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Log Viewer dialog */}
          {!isLimitedView && showLogViewer && (() => {
            const logsCmd = commands.find(c => c.command_type === 'get_logs' && c.status === 'completed' && c.result);
            if (!logsCmd) return null;
            const res = logsCmd.result as any;
            const lines: string[] = res?.lines ?? [];
            return (
              <LogViewer
                lines={lines}
                fetchedAt={logsCmd.completed_at ?? logsCmd.created_at}
                onClose={() => setShowLogViewer(false)}
              />
            );
          })()}
        </div>
      </div>

      {/* Live telemetry dialog */}
      {showLiveTelemetry && (
        <LiveTelemetryDialog
          stationId={station.id}
          stationName={station.name}
          onClose={() => setShowLiveTelemetry(false)}
        />
      )}

      {/* Screenshot lightbox */}
      {lightbox && station.screenshot_url && (
        <Dialog open onOpenChange={() => setLightbox(false)}>
          <DialogContent className="max-w-6xl p-2">
            <DialogHeader className="px-2 pt-1 pb-0">
              <DialogTitle className="text-sm">
                {station.name}
                {station.screenshot_at && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">{formatLastSeen(station.screenshot_at)}</span>
                )}
              </DialogTitle>
            </DialogHeader>
            <img src={station.screenshot_url} alt="Station screenshot" className="w-full rounded" />
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm dialog */}
      {pendingCmd && (() => {
        const conf = CONFIRM_CONFIG[pendingCmd.cmd];
        if (!conf) return null;
        return (
          <Dialog open onOpenChange={() => setPendingCmd(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{conf.title}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">{conf.description}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPendingCmd(null)}>Cancel</Button>
                <Button variant={pendingCmd.cmd === 'retire' ? 'destructive' : 'default'}
                  onClick={() => { cmdMutation.mutate({ cmd: pendingCmd.cmd }); setPendingCmd(null); }}>
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
